import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { TourId } from '@proofroom/schema';

export interface TourStep {
  /** Matches a `data-tour="<target>"` attribute on the screen */
  target: string;
  title: string;
  body: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

export interface TourProps {
  id: TourId;
  steps: TourStep[];
  open: boolean;
  onClose(finished: boolean): void;
}

interface Rect { top: number; left: number; width: number; height: number }
interface Point { top: number; left: number }

/** Breathing room between the target and the edge of the light, and between the light and the card */
const PAD = 6;
const GAP = 12;
const CARD_W = 320;

const find = (target: string): HTMLElement | null => document.querySelector<HTMLElement>(`[data-tour="${target}"]`);

function place(rect: Rect | null, placement: TourStep['placement'], cw: number, ch: number): Point {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  if (!rect) return { top: Math.max(GAP, (vh - ch) / 2), left: Math.max(GAP, (vw - cw) / 2) };
  const below = rect.top + rect.height + PAD + GAP;
  const above = rect.top - PAD - GAP - ch;
  const after = rect.left + rect.width + PAD + GAP;
  const before = rect.left - PAD - GAP - cw;
  const midX = rect.left + rect.width / 2 - cw / 2;
  const midY = rect.top + rect.height / 2 - ch / 2;
  let top: number;
  let left: number;
  // the asked-for side, or the opposite one when it would leave the viewport
  switch (placement) {
    case 'top': top = above >= GAP ? above : below; left = midX; break;
    case 'left': left = before >= GAP ? before : after; top = midY; break;
    case 'right': left = after + cw <= vw - GAP ? after : before; top = midY; break;
    default: top = below + ch <= vh - GAP ? below : above; left = midX;
  }
  return {
    top: Math.min(Math.max(GAP, top), Math.max(GAP, vh - ch - GAP)),
    left: Math.min(Math.max(GAP, left), Math.max(GAP, vw - cw - GAP)),
  };
}

/**
 * A spotlight on one `data-tour` target at a time, with a card beside it. The steps whose target
 * is not on the screen are dropped when the tour opens, so a client never sees an agency step
 * and the count says how many they will actually see. Nothing renders while closed, or when no
 * target is there at all (the tour then stays unstamped for next time).
 */
export function Tour({ id, steps, open, onClose }: TourProps) {
  const [visible, setVisible] = useState<TourStep[]>([]);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [pos, setPos] = useState<Point | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // read through a ref so a screen that builds its steps inline does not restart the tour every render
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  useEffect(() => {
    if (!open) { setVisible([]); setIndex(0); return; }
    setVisible(stepsRef.current.filter((s) => find(s.target)));
    setIndex(0);
  }, [open]);

  const step = open ? visible[index] : undefined;
  const last = index === visible.length - 1;

  // the target's box, kept fresh while the page moves under the light
  useLayoutEffect(() => {
    if (!step) { setRect(null); return; }
    const measure = () => {
      const el = find(step.target);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    find(step.target)?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step]);

  useLayoutEffect(() => {
    if (!step) { setPos(null); return; }
    const card = cardRef.current;
    setPos(place(rect, step.placement, card?.offsetWidth || CARD_W, card?.offsetHeight || 160));
  }, [step, rect]);

  useEffect(() => {
    if (step) cardRef.current?.focus({ preventScroll: true });
  }, [step]);

  const next = useCallback(() => {
    if (last) onClose(true);
    else setIndex((i) => i + 1);
  }, [last, onClose]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const skip = useCallback(() => onClose(false), [onClose]);

  // capture phase, so the viewer's own arrow and Escape handling never fires under the tour
  useEffect(() => {
    if (!step) return;
    const onKey = (e: KeyboardEvent) => {
      const onButton = e.target instanceof HTMLButtonElement && cardRef.current?.contains(e.target);
      let handled = true;
      if (e.key === 'Escape') skip();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') back();
      else if (e.key === 'Enter' && !onButton) next();
      else handled = false;
      if (handled) { e.preventDefault(); e.stopImmediatePropagation(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [step, next, back, skip]);

  if (!step) return null;
  const titleId = `tour-${id}-title`;
  return (
    <>
      <div className={`tour-backdrop${rect ? '' : ' dim'}`} />
      {rect && (
        <div
          className="tour-spot" data-target={step.target}
          style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }}
        />
      )}
      <div
        ref={cardRef} className="tour-card" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
        style={{ top: pos?.top, left: pos?.left, visibility: pos ? undefined : 'hidden' }}
      >
        <h2 id={titleId}>{step.title}</h2>
        <p>{step.body}</p>
        <div className="tour-foot">
          <span className="muted small">{index + 1} of {visible.length}</span>
          <span className="grow" />
          <button className="btn sm ghost" onClick={skip}>Skip tour</button>
          <button className="btn sm ghost" disabled={index === 0} onClick={back}>Back</button>
          <button className="btn sm primary" onClick={next}>{last ? 'Finish' : 'Next'}</button>
        </div>
      </div>
    </>
  );
}
